import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  Calendar as CalendarIcon,
  FolderKanban,
  ArrowRight,
  Search,
  Users,
  FileSpreadsheet
} from "lucide-react";

export default function MyTasksPage() {
  const { user, token, API } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProject, setFilterProject] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [projects, setProjects] = useState([]);

  useEffect(() => { fetchTasks(); fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setProjects(await response.json());
    } catch (error) { console.error("Failed to load projects"); }
  };

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${API}/tasks`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setTasks(await response.json());
    } catch (error) { toast.error("Failed to load tasks"); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) throw new Error("Failed to update status");
      toast.success("Status updated");
      fetchTasks();
    } catch (error) { toast.error(error.message); }
  };

  const handleDownloadExcel = async () => {
    try {
      toast.info("Generating Excel...");
      const response = await fetch(`${API}/tasks/export.xlsx`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to generate Excel");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `My_Tasks.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (error) { toast.error(error.message); }
  };

  const filteredTasks = tasks.filter(task => {
    if (filterStatus !== "all" && task.status !== filterStatus) return false;
    if (filterProject !== "all" && task.project_id !== filterProject) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!task.title?.toLowerCase().includes(q) && !task.project_name?.toLowerCase().includes(q) && !task.department?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const tasksByProject = filteredTasks.reduce((acc, task) => {
    const key = task.project_name || "Unknown Project";
    if (!acc[key]) acc[key] = { projectId: task.project_id, projectName: key, tasks: [] };
    acc[key].tasks.push(task);
    return acc;
  }, {});

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const overdueTasks = tasks.filter(t => t.status !== 'completed' && new Date(t.due_date) < new Date()).length;
  const pendingTasks = tasks.filter(t => t.status === 'pending' || t.status === 'in_progress').length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-500">Loading tasks...</div></div>;

  return (
    <div className="space-y-6" data-testid="my-tasks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">My Tasks</h1>
          <p className="text-sm text-slate-500 mt-0.5">All tasks assigned to you across projects</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleDownloadExcel} className="rounded-lg gap-1.5 border-slate-300 h-9 text-xs" data-testid="download-my-tasks-excel-btn">
          <FileSpreadsheet className="w-3.5 h-3.5" />Download Excel
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total", value: totalTasks, icon: <FolderKanban className="w-5 h-5 text-slate-600" />, bg: "bg-slate-50" },
          { label: "Completed", value: completedTasks, icon: <CheckCircle2 className="w-5 h-5 text-emerald-600" />, bg: "bg-emerald-50" },
          { label: "Active", value: pendingTasks, icon: <Clock className="w-5 h-5 text-amber-600" />, bg: "bg-amber-50" },
          { label: "Overdue", value: overdueTasks, icon: <AlertTriangle className="w-5 h-5 text-red-600" />, bg: "bg-red-50" },
        ].map(s => (
          <Card key={s.label} className="bg-white border-slate-200">
            <CardContent className="py-4 px-5">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${s.bg}`}>{s.icon}</div>
                <div>
                  <div className="text-xl font-bold text-slate-900">{s.value}</div>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input type="text" placeholder="Search tasks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9 rounded-lg border-slate-200 bg-white text-sm" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[130px] h-9 rounded-lg border-slate-200 bg-white text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-[160px] h-9 rounded-lg border-slate-200 bg-white text-xs"><SelectValue placeholder="Project" /></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Tasks grouped by project */}
      {Object.keys(tasksByProject).length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center">
            <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-slate-700 mb-1">No tasks found</h3>
            <p className="text-sm text-slate-500">
              {filterStatus !== "all" || filterProject !== "all" ? "Try adjusting your filters" : "No tasks assigned to you yet"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.values(tasksByProject).map(({ projectId, projectName, tasks: projectTasks }) => (
            <Card key={projectName} className="bg-white border-slate-200">
              <CardHeader className="py-4 px-5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <FolderKanban className="w-4 h-4 text-[#1e3a5f]" />{projectName}
                    <Badge variant="outline" className="rounded-md text-xs ml-1">{projectTasks.length}</Badge>
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}`)} className="text-slate-500 hover:text-[#1e3a5f] text-xs">
                    View Project<ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 px-4 pb-4">
                <div className="space-y-3">
                  {projectTasks
                    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
                    .map(task => {
                      const isOverdue = task.status !== 'completed' && new Date(task.due_date) < new Date();
                      const assigneeDisplay = (task.assignee_names || []).join(", ");
                      return (
                        <div
                          key={task.id}
                          className={`p-4 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors shadow-sm ${isOverdue ? 'border-l-4 border-l-red-500' : ''}`}
                          data-testid={`my-task-${task.id}`}
                        >
                          {/* Title and badges */}
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-semibold text-sm text-slate-900">{task.title}</h4>
                            <Badge variant="outline" className="rounded-md text-xs">{task.department}</Badge>
                            {isOverdue && (
                              <Badge className="bg-red-50 text-red-700 border-red-200 rounded-md text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" />Overdue
                              </Badge>
                            )}
                            {task.depends_on_title && (
                              <Badge className="bg-purple-50 text-purple-700 border-purple-200 rounded-md text-xs">
                                After: {task.depends_on_title}
                              </Badge>
                            )}
                          </div>

                          {/* Assignees and Date */}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
                            {assigneeDisplay && (
                              <span className="flex items-center gap-1.5">
                                <Users className="w-3.5 h-3.5" />
                                <span className="truncate max-w-[200px]">{assigneeDisplay}</span>
                              </span>
                            )}
                            <span className="flex items-center gap-1 shrink-0">
                              <CalendarIcon className="w-3.5 h-3.5" />
                              {task.start_date && `${new Date(task.start_date).toLocaleDateString('en-GB', {day:'2-digit',month:'short'})} - `}
                              {new Date(task.due_date).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric'})}
                            </span>
                          </div>

                          {/* Actions row */}
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                            <Select value={task.status} onValueChange={(value) => handleStatusChange(task.id, value)}>
                              <SelectTrigger className="w-[130px] h-8 rounded-lg border-slate-200 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-slate-200">
                                <SelectItem value="pending"><span className="flex items-center gap-2 text-xs"><Clock className="w-3 h-3 text-amber-600" />Pending</span></SelectItem>
                                <SelectItem value="in_progress"><span className="flex items-center gap-2 text-xs"><Clock className="w-3 h-3 text-blue-600" />In Progress</span></SelectItem>
                                <SelectItem value="completed"><span className="flex items-center gap-2 text-xs"><CheckCircle2 className="w-3 h-3 text-emerald-600" />Completed</span></SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}